import { Component, ErrorInfo, ReactNode } from "react";

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes. Without one, React unmounts the whole tree on any thrown render error
 * and the user is left staring at a blank white page with no indication anything went wrong and no way
 * back -- indistinguishable from the app failing to load at all.
 *
 * Deliberately a class: error boundaries have no hook equivalent, componentDidCatch is the only API
 * React exposes for this.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Goes to the browser console rather than a reporting service -- there isn't one wired up yet, and
    // swallowing it entirely would make a production crash undiagnosable.
    console.error("Unhandled render error:", error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div className="centered-page">
        <div className="card">
          <h1>Something went wrong</h1>
          <p>The page hit an unexpected error. Reloading usually clears it — if it keeps happening, let your coach know.</p>
          <p className="error">{this.state.error.message}</p>
          <button onClick={() => window.location.reload()}>Reload the page</button>
        </div>
      </div>
    );
  }
}
